export type TransactionDatePattern = 'DD/MM/YY' | 'DD/MM/YYYY, HH:mm';

export const getMerchantTimezone = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof timezone === 'string' ? timezone : '';
  } catch {
    return '';
  }
};

export const isValidTimezone = (timezone?: string | null): boolean => {
  if (!timezone) {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

export const buildTimezoneQueryParam = (
  filters: Record<string, unknown> | undefined,
  timezone: string
): string => {
  const hasDateFilter = [filters?.from, filters?.to].some(
    value => typeof value === 'string' && value !== ''
  );

  return hasDateFilter && timezone ? `timezone=${encodeURIComponent(timezone)}` : '';
};

export const parseBackendUtcDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})?$/
  );
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second, fraction, offset] = match;
  const utcDate = new Date(0);
  utcDate.setUTCFullYear(Number(year), Number(month) - 1, Number(day));
  utcDate.setUTCHours(Number(hour), Number(minute), Number(second), 0);

  if (
    utcDate.getUTCFullYear() !== Number(year) ||
    utcDate.getUTCMonth() !== Number(month) - 1 ||
    utcDate.getUTCDate() !== Number(day) ||
    Number(hour) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59
  ) {
    return null;
  }

  const milliseconds = Number((fraction?.slice(1) ?? '').padEnd(3, '0').slice(0, 3));
  const date = offset ? new Date(value) : new Date(utcDate.getTime() + milliseconds);
  return Number.isNaN(date.getTime()) ? null : date;
};

const padTwoDigits = (value: number): string => String(value).padStart(2, '0');

const formatUtcDate = (date: Date, pattern: TransactionDatePattern): string => {
  const day = padTwoDigits(date.getUTCDate());
  const month = padTwoDigits(date.getUTCMonth() + 1);
  const year = date.getUTCFullYear();

  if (pattern === 'DD/MM/YY') {
    return `${day}/${month}/${String(year).slice(-2).padStart(2, '0')}`;
  }

  return `${day}/${month}/${String(year).padStart(4, '0')}, ${padTwoDigits(date.getUTCHours())}:${padTwoDigits(
    date.getUTCMinutes()
  )}`;
};

export const formatInMerchantTimezone = (
  value: string | null | undefined,
  pattern: TransactionDatePattern,
  timezone: string = getMerchantTimezone()
): string => {
  const date = parseBackendUtcDate(value);
  if (!date) {
    return '';
  }

  const validTimezone = isValidTimezone(timezone) ? timezone : 'UTC';

  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: validTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(pattern === 'DD/MM/YYYY, HH:mm'
        ? { hour: '2-digit' as const, minute: '2-digit' as const, hourCycle: 'h23' as const }
        : {}),
    }).formatToParts(date);
    const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value ?? '';
    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');

    if (pattern === 'DD/MM/YY') {
      return `${day}/${month}/${year.slice(-2).padStart(2, '0')}`;
    }

    return `${day}/${month}/${year.padStart(4, '0')}, ${getPart('hour')}:${getPart('minute')}`;
  } catch {
    return formatUtcDate(date, pattern);
  }
};
